import { useState } from "react";
import { Play, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

interface VideoExplainerProps {
  videoId: string;
  title?: string;
  description?: string;
}

export const VideoExplainer = ({
  videoId,
  title = "Entenda como funciona",
  description = "Assista o passo a passo em 1 minuto",
}: VideoExplainerProps) => {
  const [open, setOpen] = useState(false);
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group relative w-full overflow-hidden rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md text-left transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:shadow-xl hover:shadow-black/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--brand-teal-bright))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--brand-navy-deep))]"
        aria-label={`Assistir vídeo: ${title}`}
      >
        <div className="relative aspect-video w-full">
          <img
            src={thumbnailUrl}
            alt={`Thumbnail do vídeo: ${title}`}
            loading="lazy"
            className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[hsl(var(--brand-teal-bright))] text-[hsl(var(--brand-navy-deep))] shadow-lg transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
              <Play className="ml-1 h-7 w-7 fill-current" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <p className="font-display text-sm font-semibold text-white">{title}</p>
            <p className="font-body text-xs text-white/80">{description}</p>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl border-white/10 bg-[hsl(var(--brand-navy-deep))] p-0 sm:rounded-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl">
            {open && (
              <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title={title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 h-full w-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoExplainer;
